import { unlockOrder } from "./data";

export type MissionKind = "normal" | "combo" | "rainy" | "lock";
export type Mission = {
  id: string;
  title: string;
  kind: MissionKind;
  objectives: [string, string, string?];
  featured: string[];
  modifier: string;
};
export type HomeStage = "rooms" | "ash" | "door" | "vehicle";
export type ObjectiveSource =
  | "rooms-all"
  | "items"
  | "featured"
  | "ash-bagged"
  | "door-checks"
  | "lock-step"
  | "vehicle"
  | "large-first"
  | "shield-save"
  | "dry-bathroom"
  | "calm-recovery"
  | "calm"
  | "ordered-pack"
  | "line-up"
  | "handover"
  | "route-preview"
  | "backup-route"
  | "error-found"
  | "balanced-order"
  | "green-heart"
  | "wet-obstacle"
  | "parade-mood";
export type MissionAction = {
  id: string;
  stage: HomeStage;
  source: ObjectiveSource;
  required?: string[];
  lockStep?: number;
};
export type MissionPlan = {
  actions: [MissionAction, MissionAction, MissionAction?];
  featuredStage: HomeStage;
  bonus: number;
  flow: string;
  featuredPower: number;
};

export const missions: Mission[] = [
  {
    id: "m01",
    title: "星期日探婆婆",
    kind: "normal",
    objectives: ["執齊四房出門物品", "帶走垃圾", "安全驗鎖"],
    featured: ["dai-lup", "grandma", "bibi"],
    modifier: "一家人齊齊整整",
  },
  {
    id: "m02",
    title: "大花搬運日",
    kind: "combo",
    objectives: ["大件物品先收好", "啟動比比 × 大花組合", "揀穩陣路線"],
    featured: ["dai-fa", "big-kin", "bibi"],
    modifier: "大花一次搬兩件",
  },
  {
    id: "m03",
    title: "浴室乾爽任務",
    kind: "normal",
    objectives: ["收好毛巾紙巾", "保護易碎用品", "檢查濕滑位置"],
    featured: ["fish", "abi", "chun"],
    modifier: "魚魚帶隊檢查浴室",
  },
  {
    id: "m04",
    title: "細物尋寶日",
    kind: "normal",
    objectives: ["搵鎖匙同電話", "核對出門清單"],
    featured: ["small-kin", "big-eye", "bow"],
    modifier: "大眼同細健唔會睇漏",
  },
  {
    id: "m05",
    title: "攬攬安心組合",
    kind: "combo",
    objectives: ["安撫走失成員", "帶住細粒Ppi", "保持家庭冷靜"],
    featured: ["lamlam", "ppi", "lion"],
    modifier: "攬住先，慢慢處理",
  },
  {
    id: "m06",
    title: "正式出發儀式",
    kind: "combo",
    objectives: ["按次序收納", "完成正式排隊", "交接物品"],
    featured: ["fafa", "tin-king", "bo"],
    modifier: "法法要求完整 ceremony",
  },
  {
    id: "m07",
    title: "旅行 PM 組合",
    kind: "combo",
    objectives: ["預覽兩條路線", "準備後備方案", "指出一次錯誤"],
    featured: ["fake", "prof", "dukduk"],
    modifier: "老返已經開好 tracker",
  },
  {
    id: "m08",
    title: "安靜房間日",
    kind: "normal",
    objectives: ["輕聲收拾睡房", "平衡物品次序", "等齊先出門"],
    featured: ["frog", "mid-kin", "a-tin"],
    modifier: "阿蛙提醒大家慢慢嚟",
  },
  {
    id: "m09",
    title: "雨天穩陣行",
    kind: "rainy",
    objectives: ["帶齊雨天用品", "浴室保持乾爽", "選擇較闊時機"],
    featured: ["kuk", "big-face", "car-one"],
    modifier: "落雨路滑，一仔擴闊安全時機",
  },
  {
    id: "m10",
    title: "雨天開心巡遊",
    kind: "rainy",
    objectives: ["收集綠心", "避開濕滑障礙", "保持 parade 心情"],
    featured: ["do-nga", "bean", "wong-choi"],
    modifier: "多牙負責氣氛同節奏",
  },
  {
    id: "m11",
    title: "電子鎖例外：冇聲",
    kind: "lock",
    objectives: ["先聽關門聲", "確認鎖舌圖示", "輕拉門驗證"],
    featured: ["bow", "chun", "car-horse"],
    modifier: "冇確認聲就停低重做",
  },
  {
    id: "m12",
    title: "電子鎖例外：旺財門邊",
    kind: "lock",
    objectives: ["旺財離開門罅", "垃圾放指定位置", "聲音→鎖舌→輕拉"],
    featured: ["wong-choi", "lamlam", "grandma"],
    modifier: "旺財係寵物，唔係仔",
  },
];

export function missionForDate(date = new Date()): Mission {
  const day = Math.floor(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) /
      86400000,
  );
  return missions[
    ((day % missions.length) + missions.length) % missions.length
  ];
}
export const missionRosterComplete = () =>
  unlockOrder.every((id) =>
    missions.some((mission) => mission.featured.includes(id)),
  );

/** Each card binds its copy to household actions, rather than a C1 acknowledgement. */
export const missionPlans: Record<string, MissionPlan> = {
  m01: {
    actions: [
      { id: "m01-pack-all-rooms", stage: "rooms", source: "rooms-all" },
      { id: "m01-take-rubbish", stage: "ash", source: "ash-bagged" },
      {
        id: "m01-safe-lock-check",
        stage: "door",
        source: "lock-step",
        lockStep: 3,
      },
    ],
    featuredStage: "rooms",
    bonus: 2,
    flow: "全屋清單",
    featuredPower: 1,
  },
  m02: {
    actions: [
      {
        id: "m02-pack-large-items-first",
        stage: "rooms",
        source: "large-first",
      },
      {
        id: "m02-bibi-dai-fa-combo",
        stage: "rooms",
        source: "featured",
        required: ["bibi", "dai-fa"],
      },
      {
        id: "m02-choose-stable-route",
        stage: "vehicle",
        source: "vehicle",
        required: ["car-one"],
      },
    ],
    featuredStage: "rooms",
    bonus: 8,
    flow: "雙件搬運",
    featuredPower: 2,
  },
  m03: {
    actions: [
      {
        id: "m03-pack-towel-tissue",
        stage: "rooms",
        source: "items",
        required: ["毛巾", "紙巾"],
      },
      { id: "m03-protect-fragile", stage: "rooms", source: "shield-save" },
      { id: "m03-check-wet-floor", stage: "rooms", source: "dry-bathroom" },
    ],
    featuredStage: "rooms",
    bonus: 3,
    flow: "乾爽防滑",
    featuredPower: 3,
  },
  m04: {
    actions: [
      {
        id: "m04-find-key-phone",
        stage: "rooms",
        source: "items",
        required: ["鎖匙", "電話"],
      },
      {
        id: "m04-check-departure-list",
        stage: "door",
        source: "door-checks",
        required: [
          "點齊全部仔s，確認旺財離開門罅",
          "垃圾袋放門外指定位置",
          "慢慢關門，確認冇夾到任何成員",
        ],
      },
    ],
    featuredStage: "rooms",
    bonus: 4,
    flow: "小物提示",
    featuredPower: 4,
  },
  m05: {
    actions: [
      {
        id: "m05-comfort-lost-member",
        stage: "rooms",
        source: "calm-recovery",
      },
      {
        id: "m05-carry-small-ppi",
        stage: "rooms",
        source: "featured",
        required: ["lamlam", "ppi"],
      },
      { id: "m05-keep-family-calm", stage: "door", source: "calm" },
    ],
    featuredStage: "rooms",
    bonus: 9,
    flow: "冷靜救援",
    featuredPower: 5,
  },
  m06: {
    actions: [
      { id: "m06-pack-in-order", stage: "rooms", source: "ordered-pack" },
      { id: "m06-formal-line-up", stage: "door", source: "line-up" },
      { id: "m06-handover-items", stage: "vehicle", source: "handover" },
    ],
    featuredStage: "rooms",
    bonus: 10,
    flow: "正式次序",
    featuredPower: 6,
  },
  m07: {
    actions: [
      { id: "m07-preview-two-routes", stage: "rooms", source: "route-preview" },
      { id: "m07-prepare-backup", stage: "door", source: "backup-route" },
      { id: "m07-identify-error", stage: "door", source: "error-found" },
    ],
    featuredStage: "rooms",
    bonus: 11,
    flow: "路線預覽",
    featuredPower: 7,
  },
  m08: {
    actions: [
      {
        id: "m08-quiet-bedroom",
        stage: "rooms",
        source: "items",
        required: ["鎖匙", "外套"],
      },
      { id: "m08-balance-order", stage: "rooms", source: "balanced-order" },
      {
        id: "m08-wait-for-everyone",
        stage: "door",
        source: "door-checks",
        required: ["點齊全部仔s，確認旺財離開門罅"],
      },
    ],
    featuredStage: "rooms",
    bonus: 5,
    flow: "安靜等候",
    featuredPower: 8,
  },
  m09: {
    actions: [
      {
        id: "m09-pack-rain-gear",
        stage: "door",
        source: "door-checks",
        required: ["雨傘已放入防水袋"],
      },
      {
        id: "m09-keep-bathroom-dry",
        stage: "rooms",
        source: "dry-bathroom",
      },
      {
        id: "m09-choose-wide-window",
        stage: "vehicle",
        source: "vehicle",
        required: ["car-one"],
      },
    ],
    featuredStage: "rooms",
    bonus: 7,
    flow: "雨具闊位",
    featuredPower: 9,
  },
  m10: {
    actions: [
      { id: "m10-collect-green-heart", stage: "rooms", source: "green-heart" },
      { id: "m10-avoid-wet-obstacle", stage: "door", source: "wet-obstacle" },
      {
        id: "m10-keep-parade-mood",
        stage: "vehicle",
        source: "parade-mood",
      },
    ],
    featuredStage: "vehicle",
    bonus: 12,
    flow: "雨天巡遊",
    featuredPower: 10,
  },
  m11: {
    actions: [
      {
        id: "m11-hear-close-sound",
        stage: "door",
        source: "lock-step",
        lockStep: 1,
      },
      {
        id: "m11-confirm-bolt-icon",
        stage: "door",
        source: "lock-step",
        lockStep: 2,
      },
      { id: "m11-gentle-tug", stage: "door", source: "lock-step", lockStep: 3 },
    ],
    featuredStage: "door",
    bonus: 13,
    flow: "無聲重驗",
    featuredPower: 11,
  },
  m12: {
    actions: [
      {
        id: "m12-wong-choi-clear-gap",
        stage: "door",
        source: "door-checks",
        required: ["點齊全部仔s，確認旺財離開門罅"],
      },
      {
        id: "m12-rubbish-designated-place",
        stage: "door",
        source: "door-checks",
        required: ["垃圾袋放門外指定位置"],
      },
      {
        id: "m12-sound-bolt-tug",
        stage: "door",
        source: "lock-step",
        lockStep: 3,
      },
    ],
    featuredStage: "door",
    bonus: 14,
    flow: "寵物門邊",
    featuredPower: 12,
  },
};

export type HomeSignals = {
  currentStage: HomeStage;
  completedStages: HomeStage[];
  roomsComplete: boolean;
  items: string[];
  itemOrder: string[];
  featured: Record<string, HomeStage>;
  ashBagged: boolean;
  doorChecks: string[];
  lockIndex: number;
  vehicle: string | null;
  shieldSaves: number;
  calmRecoveries: number;
  calm: number;
  routePreviews: string[];
  backupRoute: boolean;
  errorsFound: number;
  lineUp: boolean;
  handover: boolean;
  greenHearts: number;
  wetObstaclesAvoided: number;
  paradeMood: number;
};
export function objectiveSatisfied(
  action: MissionAction,
  signals: HomeSignals,
) {
  if (
    signals.currentStage !== action.stage &&
    !signals.completedStages.includes(action.stage)
  )
    return false;
  const contains = (values: string[] | undefined, actual: string[]) =>
    values?.every((value) => actual.includes(value)) ?? false;
  switch (action.source) {
    case "rooms-all":
      return signals.roomsComplete;
    case "items":
      return contains(action.required, signals.items);
    case "featured":
      return (
        action.required?.every((id) => signals.featured[id] === action.stage) ??
        false
      );
    case "ash-bagged":
      return signals.ashBagged;
    case "door-checks":
      return contains(action.required, signals.doorChecks);
    case "lock-step":
      return signals.lockIndex >= (action.lockStep || 99);
    case "vehicle":
      return !!signals.vehicle && contains(action.required, [signals.vehicle]);
    case "large-first":
      return signals.itemOrder[0] === "外套";
    case "shield-save":
      return signals.shieldSaves > 0;
    case "dry-bathroom":
      return (
        contains(["毛巾", "紙巾"], signals.items) &&
        signals.wetObstaclesAvoided > 0
      );
    case "calm-recovery":
      return signals.calmRecoveries > 0;
    case "calm":
      return signals.calm > 0;
    case "ordered-pack":
      return (
        signals.itemOrder.join("|") ===
        "垃圾袋|電話|水樽|環保袋|毛巾|紙巾|鎖匙|外套"
      );
    case "line-up":
      return signals.lineUp;
    case "handover":
      return signals.handover && !!signals.vehicle;
    case "route-preview":
      return new Set(signals.routePreviews).size >= 2;
    case "backup-route":
      return signals.backupRoute;
    case "error-found":
      return signals.errorsFound > 0;
    case "balanced-order":
      return signals.itemOrder[0] === "鎖匙" && signals.itemOrder[1] === "外套";
    case "green-heart":
      return signals.greenHearts > 0;
    case "wet-obstacle":
      return signals.wetObstaclesAvoided > 0;
    case "parade-mood":
      return (
        signals.paradeMood > 0 &&
        Object.values(signals.featured).filter(
          (stage) => stage === action.stage,
        ).length >= 3
      );
  }
}
export function completedMissionObjectives(
  mission: Mission,
  signals: HomeSignals,
) {
  const plan = missionPlans[mission.id];
  return mission.objectives.filter(
    (objective, index) =>
      objective && objectiveSatisfied(plan.actions[index]!, signals),
  );
}
